import { type JSX, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { Button, Spinner } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import {
  useMemberships,
  useSwitchTenant,
  type Membership,
} from '@/hooks/useMemberships';
import { cn } from '@/lib/utils';

/**
 * Post-login picker for users with 2+ tenant memberships. Login lands
 * here when the membership list has more than one entry; single-
 * membership users skip straight to /. Picking a workspace either
 * navigates home (if it matches the JWT's current tid) or reissues the
 * token pair via /v1/auth/switch-tenant and reloads at /.
 *
 * The page lives outside the AppShell on purpose — sidebar and topbar
 * are scoped to the active tid, and showing them here would imply a
 * choice has already been made. The layout matches the login page so
 * the auth flow feels continuous.
 */
export default function SelectWorkspacePage(): JSX.Element {
  const { t } = useTranslation('default');
  const navigate = useNavigate();
  const { user, logout, isLoggingOut } = useAuth();
  const list = useMemberships();
  // Picker target is "/" so HomeRedirect can pick the right landing
  // page (admin/dashboard for resellers/platform, /dashboard for
  // customers) using the freshly-minted claims.
  const switchTenant = useSwitchTenant({ redirectTo: '/' });

  // Defensive fallback: nothing to pick from. Login should have routed
  // here only when memberships > 1, but if a user somehow lands here
  // with 0 or 1 membership (deep link, race), just go home.
  useEffect(() => {
    if (list.data && list.data.items.length <= 1) {
      navigate('/', { replace: true });
    }
  }, [list.data, navigate]);

  const handlePick = (m: Membership): void => {
    if (m.tenant_id === user?.tid) {
      // Same tenant the JWT is already scoped to — no token swap
      // needed, just enter the app.
      navigate('/', { replace: true });
      return;
    }
    switchTenant.mutate(m.tenant_id);
  };

  const items = list.data?.items ?? [];

  return (
    <div className="w-full min-h-screen bg-paper flex flex-col">
      <header className="px-8 py-5 border-b border-line bg-surface flex items-center justify-between">
        <BrandMark />
        <div className="flex items-center gap-3">
          <span className="text-[12.5px] text-text-3 hidden sm:inline">
            {user?.email ?? ''}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void logout()}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? t('um_logging_out') : t('um_logout')}
          </Button>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-6 py-14">
        <div className="w-full max-w-[520px]">
          <h1 className="display text-[28px] font-semibold tracking-[-0.025em] text-text leading-tight m-0">
            {t('sw_title')}
          </h1>
          <p className="text-[14px] text-text-3 mt-2 leading-relaxed">
            {t('sw_subtitle')}
          </p>

          {list.isError && (
            <div className="mt-6 px-3.5 py-3 bg-danger-bg border border-danger-bd rounded-md flex items-start gap-2.5">
              <Icon.warn width="18" height="18" className="text-danger flex-shrink-0 mt-px" />
              <div className="flex-1 text-[13px] text-danger leading-relaxed">
                {t('sw_load_error')}
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void list.refetch()}
                disabled={list.isFetching}
              >
                {list.isFetching ? t('dash_load_retrying') : t('dash_load_retry')}
              </Button>
            </div>
          )}

          {switchTenant.isError && (
            <div
              role="alert"
              className="mt-6 px-3.5 py-3 bg-danger-bg border border-danger-bd rounded-md flex items-start gap-2.5"
            >
              <Icon.warn width="18" height="18" className="text-danger flex-shrink-0 mt-px" />
              <div className="text-[13px] text-danger leading-relaxed">
                {t('sw_switch_error')}
              </div>
            </div>
          )}

          <div className="mt-7">
            {list.isLoading ? (
              <div className="py-12 text-center">
                <Spinner />
              </div>
            ) : items.length === 0 ? null : (
              <ul className="flex flex-col gap-2">
                {items.map((m) => (
                  <li key={m.tenant_id}>
                    <WorkspaceRow
                      membership={m}
                      isCurrent={m.tenant_id === user?.tid}
                      disabled={switchTenant.isPending}
                      onClick={() => handlePick(m)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

interface WorkspaceRowProps {
  membership: Membership;
  isCurrent: boolean;
  disabled: boolean;
  onClick: () => void;
}

function WorkspaceRow({
  membership,
  isCurrent,
  disabled,
  onClick,
}: WorkspaceRowProps): JSX.Element {
  const { t } = useTranslation('default');
  const initial = membership.tenant_name.trim().charAt(0).toUpperCase() || '?';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'group w-full flex items-center gap-3.5 px-4 py-3.5 text-left',
        'bg-surface border border-line rounded-lg',
        'hover:border-volt-500 hover:shadow-sm transition-all',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-line disabled:hover:shadow-none',
      )}
    >
      <span
        aria-hidden="true"
        className="w-10 h-10 rounded-md grid place-items-center text-volt-ink text-[14px] font-bold flex-shrink-0"
        style={{ background: 'linear-gradient(135deg, var(--volt) 0%, var(--volt-600) 100%)' }}
      >
        {initial}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-semibold text-text truncate">
            {membership.tenant_name}
          </span>
          {isCurrent && (
            <span className="mono text-[9.5px] uppercase tracking-[0.05em] font-semibold px-1.5 py-0.5 bg-volt-100 text-volt-700 rounded">
              {t('sw_badge_current')}
            </span>
          )}
        </div>
        <div className="text-[12px] text-text-3 mt-0.5 truncate">
          <span>{t(`tenant_type_${membership.tenant_type}`)}</span>
          {membership.role && (
            <>
              <span className="mx-1.5 text-text-4">·</span>
              <span className="capitalize">{membership.role}</span>
            </>
          )}
        </div>
      </div>
      <Icon.arrowRight
        width="16"
        height="16"
        className="text-text-4 group-hover:text-volt-500 transition-colors flex-shrink-0"
      />
    </button>
  );
}

function BrandMark(): JSX.Element {
  return (
    <div className="inline-flex items-center gap-2.5">
      <svg width={28} height={28} viewBox="0 0 64 64" fill="none" aria-label="EzSender">
        <circle cx="32" cy="32" r="30" fill="var(--ink-2)" />
        <rect x="14" y="14" width="36" height="36" rx="8" fill="var(--volt)" />
        <path
          d="M22 38 L42 26 M42 26 L36 26 M42 26 L42 32"
          stroke="var(--volt-ink)"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="display text-[18px] font-semibold tracking-tight text-text">
        ezsender
      </span>
    </div>
  );
}
